import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Compass, ArrowUpRight } from "@phosphor-icons/react";

/**
 * Firm-wide Method strip for the Dashboard.
 *
 * Feb 2026 — restored to its original minimal presentation, then extended with
 * a quiet count-inside-node + hover/pin interaction (spec from the partners).
 *
 * The invariants of "minimal presentation":
 *   • A single horizontal Method diagram — two embrace shapes, four circular
 *     nodes, movement names + framework descriptors, four surrounding
 *     framework labels. Nothing else lives on the map.
 *   • No extra boxes, cards, badges, count chips, or connector shapes.
 *   • Engagement counts appear INSIDE their node — subtle, legible, theme-
 *     aware, without enlarging the node beyond what's needed to hold the digit.
 *   • The open space below the diagram is where engagement rosters appear —
 *     never as boxes attached to the map.
 *
 * Interaction model:
 *   • Hover a movement node → its active engagements preview below the map.
 *   • Click a movement node → pin that movement (Brass ring) so the roster
 *     stays visible when the cursor moves away.
 *   • Click the same node again → clear the pin.
 *   • Click another node → switch the pinned movement.
 *   • Click outside the Method container → clear the pin.
 *   • Clicking any engagement in the roster opens its engagement record.
 */

const CSS = {
  foreground:    "hsl(var(--foreground))",
  muted:         "hsl(var(--muted-foreground))",
  card:          "hsl(var(--card))",
  brass:         "hsl(var(--brass))",
  neutralStroke: "hsl(var(--muted-foreground) / 0.55)",
  neutralSoft:   "hsl(var(--muted-foreground) / 0.28)",
};

// Diagram geometry. Baseline moved down and viewBox extended so the top +
// bottom framework labels have generous, symmetric white space away from
// the embrace shapes.
const NODE_X = { listen: 280, reveal: 600, reimagine: 800, guide: 1120 };
const BASELINE_Y = 200;
const ARC_AMPLITUDE = 78;
const NODE_R = 12;           // just big enough to hold a legible one-digit
const PIVOT_GAP = 6;
const CENTER_X = (NODE_X.reveal + NODE_X.reimagine) / 2;  // 700

const lensPath = (x1, x2, direction) => {
  const y1 = BASELINE_Y + (direction === "top" ? -ARC_AMPLITUDE : ARC_AMPLITUDE);
  const c1x = x1 + (x2 - x1) * 0.25;
  const c2x = x1 + (x2 - x1) * 0.75;
  return `M ${x1} ${BASELINE_Y} C ${c1x} ${y1}, ${c2x} ${y1}, ${x2} ${BASELINE_Y}`;
};
const LENS_1_TOP = lensPath(NODE_X.listen, NODE_X.reveal, "top");
const LENS_1_BOT = lensPath(NODE_X.listen, NODE_X.reveal, "bot");
const LENS_2_TOP = lensPath(NODE_X.reimagine, NODE_X.guide, "top");
const LENS_2_BOT = lensPath(NODE_X.reimagine, NODE_X.guide, "bot");
const PIVOT_X1 = NODE_X.reveal + NODE_R + PIVOT_GAP;
const PIVOT_X2 = NODE_X.reimagine - NODE_R - PIVOT_GAP;

const PHASE_KEYS = ["listen", "reveal", "reimagine", "guide"];
const PHASE_LABELS = {
  listen:   { client: "Listen",    framework: "Discover" },
  reveal:   { client: "Reveal",    framework: "Define" },
  reimagine:{ client: "Reimagine", framework: "Develop" },
  guide:    { client: "Guide",     framework: "Deliver" },
};

const DEFAULT_FRAMEWORK_LABELS = {
  top: "Connections and Relationships",
  bottom: "Leadership and Storytelling",
  left: "Orientation and Vision Setting",
  right: "Continuing the Journey",
};

export default function MethodDashboardStrip() {
  const [dist, setDist] = useState(null);
  const [frameworkLabels, setFrameworkLabels] = useState(DEFAULT_FRAMEWORK_LABELS);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/method/firm-distribution")
      .then((r) => setDist(r.data))
      .catch(() => setDist({ buckets: {}, totals: {}, engagement_count: 0 }));
    api.get("/method/template")
      .then((r) => { if (r.data?.framework_labels) setFrameworkLabels(r.data.framework_labels); })
      .catch(() => {});
  }, []);

  // Clear the pin when the partner clicks outside the Method container.
  // Uses `mousedown` (not `click`) so the pin releases even if the click
  // starts on the container and ends outside during a drag.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPinned(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pinned]);

  const totals = dist?.totals || {};
  const unsetCount = totals.unset || 0;

  // The movement whose roster is currently visible below the map.
  // Pinned wins over hover so a partner talking through a movement isn't
  // interrupted by a stray mouse-over on the way to click somewhere else.
  const activeKey = pinned || hover;
  const activeBucket = useMemo(() => (activeKey ? (dist?.buckets?.[activeKey] || []) : []), [activeKey, dist]);
  const activeLabel = activeKey ? PHASE_LABELS[activeKey]?.client : null;

  const togglePin = (k) => setPinned((cur) => (cur === k ? null : k));

  return (
    <div ref={containerRef} className="border border-border bg-card p-6" data-testid="method-dashboard-strip">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-muted-foreground flex items-center gap-1.5">
            <Compass size={12} weight="fill" /> Firm — across the method
          </div>
          <div className="mt-1 text-xs italic text-muted-foreground" style={{ fontFamily: "Georgia, serif" }}>
            Where each engagement's primary <em>emphasis</em> sits today · hover to preview · click a movement to pin it
          </div>
        </div>
        <Link
          to="/method"
          className="text-[0.6875rem] font-mono uppercase tracking-widest text-foreground hover:text-brass inline-flex items-center gap-1"
          data-testid="method-strip-open-method"
        >
          Open method <ArrowUpRight size={12} />
        </Link>
      </div>

      <svg
        viewBox="0 0 1400 420"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Firm-wide method emphasis strip"
        style={{ width: "100%", height: "auto", maxHeight: 340, display: "block" }}
      >
        {/*
          Framework labels — the two continuous supporting dimensions
          (Connections & Relationships above, Leadership & Storytelling
          below), each centered on the vertical axis running between Reveal
          and Reimagine, with equal, generous distance from that axis so the
          symmetry reads at a glance. Kept as plain foreignObject text with
          no connector line or container — this is DELIBERATE per spec.
        */}
        <foreignObject x={CENTER_X - 220} y="26" width="440" height="24">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.6875rem",
            color: CSS.muted, letterSpacing: "0.26em", textTransform: "uppercase",
            textAlign: "center", lineHeight: 1.3,
          }} data-testid="dash-framework-label-top">
            {frameworkLabels.top}
          </div>
        </foreignObject>
        <foreignObject x={CENTER_X - 220} y="366" width="440" height="24">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.6875rem",
            color: CSS.muted, letterSpacing: "0.26em", textTransform: "uppercase",
            textAlign: "center", lineHeight: 1.3,
          }} data-testid="dash-framework-label-bottom">
            {frameworkLabels.bottom}
          </div>
        </foreignObject>

        {/* Left / Right framework labels — sit outside the outermost nodes,
            vertically centered on the baseline. Unchanged from before. */}
        <foreignObject x="10" y={BASELINE_Y - 24} width="220" height="48">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.5938rem",
            color: CSS.muted, letterSpacing: "0.22em", textTransform: "uppercase",
            textAlign: "right", lineHeight: 1.45, paddingRight: 14,
            display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%",
          }} data-testid="dash-framework-label-left">
            <span>{frameworkLabels.left}</span>
          </div>
        </foreignObject>
        <foreignObject x="1170" y={BASELINE_Y - 24} width="220" height="48">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.5938rem",
            color: CSS.muted, letterSpacing: "0.22em", textTransform: "uppercase",
            textAlign: "left", lineHeight: 1.45, paddingLeft: 14,
            display: "flex", alignItems: "center", justifyContent: "flex-start", height: "100%",
          }} data-testid="dash-framework-label-right">
            <span>{frameworkLabels.right}</span>
          </div>
        </foreignObject>

        {/* Baseline spine + arcs — restrained neutral, unchanged. */}
        <line x1={NODE_X.listen - 40} y1={BASELINE_Y} x2={NODE_X.guide + 40} y2={BASELINE_Y}
              style={{ stroke: CSS.neutralSoft }} strokeWidth="1" />
        <path d={LENS_1_TOP} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.25" />
        <path d={LENS_1_BOT} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.25" />
        <path d={`M ${PIVOT_X1} ${BASELINE_Y} L ${PIVOT_X2} ${BASELINE_Y}`}
              style={{ stroke: CSS.neutralSoft }} strokeWidth="1" strokeDasharray="3 5" />
        <path d={LENS_2_TOP} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.25" />
        <path d={LENS_2_BOT} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.25" />

        {PHASE_KEYS.map((k) => {
          const x = NODE_X[k];
          const count = totals[k] || 0;
          const isHover = hover === k;
          const isPinned = pinned === k;
          const isActive = isPinned || isHover;
          // Pinned uses brass as the single "recognition" moment — that's the
          // partner having chosen this movement to talk through. Hover uses
          // the foreground stroke so it reads as a passing look, not a
          // decision. Muted stroke is the resting state.
          const stroke = isPinned ? CSS.brass : (isActive ? CSS.foreground : CSS.muted);
          // Transparent hit-target rect spans the whole visual column
          // (circle + name + descriptor) so hover / click land reliably —
          // SVG text alone uses `pointer-events: visiblePainted` and misses
          // between glyphs, which is why direct handlers on the group were
          // unreliable. Height reaches from just above the top arc down to
          // just past the descriptor, width is generous but does not overlap
          // the next node.
          return (
            <g key={k}>
              <rect
                x={x - 70} y={BASELINE_Y - 22} width={140} height={100}
                fill="transparent" pointerEvents="all"
                onMouseEnter={() => setHover(k)}
                onMouseLeave={() => setHover((h) => (h === k ? null : h))}
                onClick={() => togglePin(k)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePin(k); } }}
                // outline suppressed so the transparent hit-target never draws
                // a browser focus rectangle over the clean map — the pin state
                // is communicated through the node's Brass stroke + the
                // "PINNED" chip in the roster panel below.
                style={{ cursor: "pointer", outline: "none" }}
                role="button"
                tabIndex={0}
                aria-pressed={isPinned}
                aria-label={`${PHASE_LABELS[k].client} — ${count} active engagement${count === 1 ? "" : "s"}`}
                data-testid={`method-strip-node-${k}`}
                data-active={isActive ? "true" : "false"}
                data-pinned={isPinned ? "true" : "false"}
              />
              <circle cx={x} cy={BASELINE_Y} r={NODE_R}
                      style={{ fill: CSS.card, stroke, pointerEvents: "none" }}
                      strokeWidth={isPinned ? 2 : 1.5} />
              {/* Count sits INSIDE the node — subtle, tabular, theme-aware.
                  Suppressed when 0 so an idle movement reads as an unfilled
                  circle rather than a decorated dot. */}
              {count > 0 && (
                <text x={x} y={BASELINE_Y + 4} textAnchor="middle"
                      style={{
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontSize: "0.75rem", fontWeight: 600,
                        fill: isPinned ? CSS.brass : CSS.foreground,
                        pointerEvents: "none",
                      }}>
                  {count}
                </text>
              )}
              <text x={x} y={BASELINE_Y + 48} textAnchor="middle"
                    style={{
                      fontFamily: "Georgia, serif", fontSize: "1.125rem", fontWeight: 700,
                      fill: CSS.foreground, pointerEvents: "none",
                    }}>
                {PHASE_LABELS[k].client}
              </text>
              <text x={x} y={BASELINE_Y + 68} textAnchor="middle"
                    style={{
                      fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.5625rem",
                      fill: CSS.muted, letterSpacing: "0.24em", textTransform: "uppercase",
                      pointerEvents: "none",
                    }}>
                {PHASE_LABELS[k].framework}
              </text>
            </g>
          );
        })}
      </svg>

      {/*
        Roster panel — the ONLY thing that lives in the open space below the
        diagram. Hover or pinned selection reveals the active-engagement
        list for that movement. No panel, no border, no visual chrome added
        to the map itself.
      */}
      <div className="mt-4 min-h-[80px]" data-testid="method-strip-roster">
        {!dist ? (
          <div className="text-xs text-muted-foreground">Loading firm distribution…</div>
        ) : !activeKey ? (
          <div className="text-[0.6875rem] italic text-muted-foreground text-center py-4" style={{ fontFamily: "Georgia, serif" }}>
            Hover a movement to preview its active engagements, or click to pin.
          </div>
        ) : (
          <div data-testid={`method-strip-active-${activeKey}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em]"
                   style={{ color: pinned ? CSS.brass : CSS.foreground }}>
                {activeLabel} · {activeBucket.length} active
              </div>
              {pinned && (
                <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-brass border border-brass/60 px-1.5 py-0.5" data-testid="method-strip-pinned-chip">
                  pinned
                </span>
              )}
            </div>
            {activeBucket.length === 0 ? (
              <div className="text-[0.75rem] italic text-muted-foreground py-2" style={{ fontFamily: "Georgia, serif" }} data-testid={`method-strip-empty-${activeKey}`}>
                No active engagement is currently emphasising {activeLabel}.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {activeBucket.map((eng) => (
                  <li key={eng.id} className="flex items-baseline gap-2 text-[0.8125rem]" data-testid={`method-strip-engagement-${eng.id}`}>
                    <Link
                      to={`/engagements/${eng.id}`}
                      className="text-foreground hover:text-action font-medium leading-snug"
                    >
                      {eng.name}
                    </Link>
                    <span className="text-muted-foreground font-serif italic text-[0.75rem]">· {eng.client_name || "—"}</span>
                    {eng.has_pending_suggestion && (
                      <span
                        className="text-brass"
                        title="Pending Managing Principal approval (unapproved YadidAi suggestion — not counted as emphasis)"
                      >●</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {dist && unsetCount > 0 && !activeKey && (
          <div className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-amber-500/90 text-center" data-testid="method-strip-unset-note">
            {unsetCount} active engagement{unsetCount === 1 ? "" : "s"} without a primary emphasis yet
          </div>
        )}
      </div>
    </div>
  );
}
